import { FC, useRef, useState } from "react";
import { ReactComponent as ThreeDotsIcon } from "images/ThreeDots.svg";
import { ReactComponent as EditIcon } from "images/EditorIcon.svg";
import { ReactComponent as PlusIcon } from "images/PlusIcon.svg";
import { ReactComponent as DownloadIcon } from "images/downloadArrowOnCircle.svg";
import { ReactComponent as UploadIcon } from "images/UploadIcon.svg";
import { ReactComponent as TrashCan } from "images/TrashCan.svg";
import { ReactComponent as PrintIcon } from "images/PrintIcon.svg";
import { AddingObjectList } from "../AddingObjectList/AddingObjectList";
import { ModalWindow } from "components/ModalWindow/ModalWindow";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import {
	deleteAddition,
	dowloadDocument,
	printDocument,
	uploadDocument,
} from "toolkit/steps/stepThree/stepThree.thunk";
import { convertDate } from "helpers";
import { DeletingLine } from "pages/StepPages/StepTwo/BusinessProcesses/DeletingLine/DeletingLine";
import { $api } from "libs/axios";
import ConfirmNavigate from "components/ConfirmNavigate";
import styles from "./StepThreeMenu.module.sass";
import cn from "classnames";
import { DownloadBackground } from "components-new/DownloadBackground/DownloadBackground";
import { FETCH_TIME_IN_MILLISECOND } from "constant";

interface IModalWindow {
	content: React.ReactElement | null;
	visibility: boolean;
}

export const StepThreeMenu: FC = () => {
	const { currentEnumList, enumListObjects } = useAppSelector((state) => state.stepThree);
	const profile = useAppSelector((state) => state.profile.profile);
	const [desireDelete, setDesireDelete] = useState<boolean>(false);
	const [isPending, setIsPending] = useState<boolean>(false);
	const dispatch = useAppDispatch();
	const [confirmAction, setConfirmAction] = useState<boolean>(false);
	const [modalWindow, setModalWindow] = useState<IModalWindow>({
		content: null,
		visibility: false,
	});

	const openModalWindow = (component: React.ReactElement) => {
		setModalWindow({ ...modalWindow, content: component, visibility: true });
	};

	const closeModalWindow = () => {
		setModalWindow({ ...modalWindow, content: null, visibility: false });
	};

	const deleteHandler = () => {
		if (!currentEnumList) return;
		dispatch(
			deleteAddition({
				enumListId: currentEnumList?.id,
			}),
		);
	};

	const downloadFile = () => {
		if (currentEnumList) {
			const fileName: string = `${currentEnumList.type === 1 ? "Перечень" : "Дополнение в перечень"} “${
				currentEnumList?.listName
			}” от ${convertDate(currentEnumList?.creationDate)}`;
			setIsPending(true);
			dispatch(
				dowloadDocument({
					enumListId: currentEnumList?.id,
					fileName,
					setIsPending,
				}),
			);
		}
	};

	const printFile = () => {
		if (currentEnumList) {
			const fileName: string = `${currentEnumList.type === 1 ? "Перечень" : "Дополнение в перечень"} “${
				currentEnumList?.listName
			}” от ${convertDate(currentEnumList?.creationDate)}`;
			setIsPending(true);
			dispatch(
				printDocument({
					enumListId: currentEnumList?.id,
					fileName,
					setIsPending,
				}),
			);
		}
	};

	const documentHandler = (files: FileList | null) => {
		if (!currentEnumList) return;
		setIsPending(true);
		approveList(files![0]);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const intefvalRef: any = useRef();
	const approveList = (file: File) => {
		intefvalRef.current = setInterval(() => {
			dispatch(
				uploadDocument({
					enumListId: Number(currentEnumList?.id),
					file,
					userName: `${profile.lastName} ${profile.firstName} ${profile.middleName}`,
					setIsPending,
					toClear: () => clearInterval(intefvalRef.current),
				}),
			);
		}, FETCH_TIME_IN_MILLISECOND);
	};

	const getPermission = () => {
		$api.get(`/kii_enums/repeatable/${currentEnumList?.id}/check`).then((r) => {
			setConfirmAction(r.data.repeatable);
			if (!r.data.repeatable) inputRef?.current?.click();
		});
	};

	const confirmClick = () => {
		setConfirmAction(false);
		// открытие проводника
		inputRef?.current?.click();
	};

	const inputRef = useRef<HTMLInputElement | null>(null);

	return (
		<>
			{modalWindow.visibility && (
				<ModalWindow close={closeModalWindow} scroll={false}>
					{modalWindow.content}
				</ModalWindow>
			)}
			{desireDelete && (
				<ModalWindow close={() => setDesireDelete(false)}>
					<DeletingLine
						close={() => setDesireDelete(false)}
						closeAll={() => setDesireDelete(false)}
						toDelete={deleteHandler}
						title={currentEnumList?.type === 1 ? "Удалить перечень?" : "Удалить дополнение?"}
					/>
				</ModalWindow>
			)}
			<ConfirmNavigate
				title="Утвердить перечень?"
				subtitle="После утверждения данного перечня статусы последующих шагов будут сброшены"
				toggle={confirmAction}
				close={() => setConfirmAction(false)}
				clickHandler={confirmClick}
			/>
			{isPending && <DownloadBackground />}
			<div className={styles["main-menu-wrp"]}>
				<button className={styles["main-menu-btn"]}>
					<ThreeDotsIcon />
				</button>
				<div className={styles["main-menu"]}>
					<button
						onClick={() =>
							openModalWindow(
								<AddingObjectList
									close={closeModalWindow}
									title="Дополнить перечень"
									subtitle="Выберите элементы для изменения"
									mode="changing"
								/>,
							)
						}
						className={styles["main-menu-item"]}>
						<div>
							<EditIcon className={styles.blue} />
						</div>
						<span className={styles["main-menu-item-txt"]}>
							{currentEnumList?.type === 1 ? "Изменить состав перечня" : "Изменить состав дополнения"}
						</span>
					</button>
					<button
						onClick={() =>
							openModalWindow(
								<AddingObjectList
									close={closeModalWindow}
									title="Создание дополнения перечня"
									subtitle="Выберите элементы для создания дополнения"
									mode="addition"
								/>,
							)
						}
						className={styles["main-menu-item"]}>
						<div>
							<PlusIcon className={styles.blue} />
						</div>
						<span className={styles["main-menu-item-txt"]}>Создать дополнение в перечень</span>
					</button>
					<button onClick={downloadFile} className={styles["main-menu-item"]}>
						<div>
							<DownloadIcon className={styles.green} />
						</div>
						<span className={styles["main-menu-item-txt"]}>
							{currentEnumList?.type === 1 ? "Скачать перечень" : "Скачать дополнение"}
						</span>
					</button>
					<input
						ref={inputRef}
						onChange={(e) => documentHandler(e.target.files)}
						type="file"
						id={`uploadDoc`}
						className="none"
						accept=".docx, .pdf"
					/>
					<label
						onClick={getPermission}
						className={cn(styles["main-menu-item"], !enumListObjects.length && styles.disabled)}>
						<div>
							<UploadIcon className={styles.blue} />
						</div>
						<span className={styles["main-menu-item-txt"]}>Загрузить утвержденную версию</span>
					</label>
					<button onClick={printFile} className={styles["main-menu-item"]}>
						<div>
							<PrintIcon className={styles.blue} />
						</div>
						<span className={styles["main-menu-item-txt"]}>Печать в А4</span>
					</button>
					{!currentEnumList?.newestDocApprovalDate && currentEnumList?.type !== 1 && (
						<button onClick={() => setDesireDelete(true)} className={styles["main-menu-item"]}>
							<div>
								<TrashCan className={styles.red} />
							</div>
							<span className={styles["main-menu-item-txt"]}>
								{currentEnumList?.type === 1 ? "Удалить перечень" : "Удалить дополнение"}
							</span>
						</button>
					)}
				</div>
			</div>
		</>
	);
};
